import { type PropsWithChildren, useMemo, useState } from 'react';
import { Bolt, LoadingSpinner } from './Icons';
import toast from 'react-hot-toast';
// TODO: Refactor so it can use context instead of importing top app
import type { AppProps } from '../app';
import { useBeehiiv } from '../hooks/useBeehiiv';
import { useEventStore } from '@stores/event';
import { AgentUIEventTypes } from '@config/events';
import { useUserStore } from '@stores/user';
import Link, { Target } from './Link';
import { Box } from './Box';
import { cn } from '@utils/cn';
import { FaArrowRight } from 'react-icons/fa6';
import { formatCurrency } from '@utils/currency';
import { create } from 'zustand';
import { useCanDeployAgent } from '@base/hooks/useCanDeployAgent';
import { useCredits, useCreditsCheckout } from '@base/hooks/useCredits';
import { createSubscription, updateSubscriptionQuantity } from '@base/api';
import { useSubscriptions } from '@base/hooks/useSubscriptions';
import { Text } from './Text';
import { Button } from './Button';
import { openPopUpTab } from '@utils/openPopUpTab';
import { useAutofunUser } from '@base/hooks/useAutofunUser';
import { AutofunSubscriptionModal } from './AutofunSubscriptionModal';
import { Modal } from './Modal';
import { useStandardAgentPlan } from '@base/hooks/useStandardAgentPlan';
import { useTeeAgentPlan } from '@base/hooks/useTeeAgentPlan';
import { Alert } from './ui/Alert';

interface SubscriptionModalProps {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  activeProjectId?: string;
  onSuccess?: (value?: boolean) => void;
  tee?: boolean;
  createSubscriptionCheckout: AppProps['createSubscriptionCheckout'];
  getReferralId?: () => string;
}

const LOADING_COPY_MAP = {
  CHECKOUT: {
    title: 'Pending payment',
    description: 'Continue making your payment to deploy your AI agent.',
  },
  TOP_CREDITS: {
    title: 'Pending payment',
    description: 'Continue making your payment to add credit balance.',
  },
  AVAILABLE_CREDITS: {
    title: 'Using credits',
    description: 'Creating subscription using available credits',
  },
};

export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isVisible,
  setIsVisible,
  onSuccess,
  activeProjectId,
  tee,
  createSubscriptionCheckout,
  getReferralId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { authStoreInstance } = useUserStore();
  const { dispatchEvent } = useEventStore();
  const { updateUser } = useBeehiiv();
  const { refetch: refetchCanDeployAgent } = useCanDeployAgent();
  const { credits, isCreditsPending, refetchCredits } = useCredits();
  const [asyncActionType, setAsyncActionType] = useState<
    'TOP_CREDITS' | 'CHECKOUT' | 'AVAILABLE_CREDITS'
  >();
  const { data: subscriptions, isPending: isSubscriptionsPending } =
    useSubscriptions();
  const { handleAddCredits: addCredits, isCreatingCheckout } =
    useCreditsCheckout();

  const { isAutofunUser } = useAutofunUser();

  const handleAddCredits = () => {
    setAsyncActionType('TOP_CREDITS');
    setIsLoading(true);

    addCredits({
      onClose: () => {
        setIsLoading(false);
      },
    });
  };

  const { data: teeAgentPlan } = useTeeAgentPlan();
  const { data: standardAgentPlan } = useStandardAgentPlan();
  const agentPlan = tee ? teeAgentPlan : standardAgentPlan;

  const hasAvailableCredits = useMemo(() => {
    if (!credits?.balance || !agentPlan) return null;

    return credits?.balance >= agentPlan?.price;
  }, [credits?.balance, agentPlan]);

  const handleSubscriptionWithCredits = async () => {
    const referralId = getReferralId ? getReferralId() : '';

    if (isSubscriptionsPending || !subscriptions) return;

    const currentSubscription = subscriptions.find(
      (sub) =>
        sub.productId === agentPlan?.id &&
        sub.status.toLowerCase() !== 'canceled',
    );

    if (currentSubscription) {
      const updateQuantityResult = await updateSubscriptionQuantity({
        projectId: activeProjectId,
        subscriptionId: currentSubscription.id,
        updateBy: 1,
        accessToken: authStoreInstance?.accessToken,
      });

      if (!updateQuantityResult.ok) {
        throw Error('Failed to update subscription quantity');
      }

      refetchCredits();

      return;
    }

    const createSubscriptionResult = await createSubscription({
      projectId: activeProjectId,
      productId: agentPlan?.id,
      accessToken: authStoreInstance?.accessToken,
      referralId,
    });

    if (!createSubscriptionResult.ok) {
      throw Error('Failed to create subscription with credits');
    }

    refetchCredits();
  };

  const handleCheckoutSubscription = async () => {
    const referralId = getReferralId ? getReferralId() : '';

    const mutationResult = await createSubscriptionCheckout(
      {
        projectId: activeProjectId,
        productId: agentPlan?.id,
        accessToken: authStoreInstance?.accessToken,
        referralId,
      },
      {
        throwOnError: true,
      },
    );

    openPopUpTab({
      url: mutationResult,
      onClose: () => {
        setIsLoading(false);
      },
    });
  };

  const handleOnSubmitClick = async () => {
    const POLLING_INTERVAL = 5000;
    const MAX_ATTEMPTS = 20;

    if (!activeProjectId || !authStoreInstance?.accessToken) return;

    setAsyncActionType(hasAvailableCredits ? 'AVAILABLE_CREDITS' : 'CHECKOUT');
    setIsLoading(true);

    try {
      hasAvailableCredits
        ? await handleSubscriptionWithCredits()
        : await handleCheckoutSubscription();
    } catch (error) {
      setIsLoading(false);
      toast.error('Failed to create subscription');
      console.error(error);
    }

    updateUser({
      email: authStoreInstance.userProfile?.email,
      abandonedCart: true,
    });

    const pollSubscriptionStatus = async (attempt: number) => {
      try {
        const { canDeployTeeAgent, canDeployStandardAgent } =
          await refetchCanDeployAgent();

        const canDeployAgent = tee ? canDeployTeeAgent : canDeployStandardAgent;
        if (canDeployAgent) {
          setIsLoading(false);
          setIsVisible(false);
          onSuccess?.(true);
          dispatchEvent(
            AgentUIEventTypes.SUBSCRIPTION.PURCHASE_POLLING.COMPLETED,
            {
              msg: 'success',
              attempt,
              conversion_value: agentPlan?.price,
              user_email: authStoreInstance.userProfile?.email,
            },
          );
          toast.success('Subscription activated successfully!');
          updateUser({
            email: authStoreInstance.userProfile?.email,
            abandonedCart: false,
          });
          return;
        }

        if (attempt >= MAX_ATTEMPTS) {
          setIsLoading(false);
          dispatchEvent(
            AgentUIEventTypes.SUBSCRIPTION.PURCHASE_POLLING.COMPLETED,
            {
              msg: 'failure',
              err: `MAX_ATTEMPTS (${MAX_ATTEMPTS}) reached.`,
            },
          );
          toast.error('Subscription verification timed out. Please try again.');
          return;
        }

        setTimeout(() => pollSubscriptionStatus(attempt + 1), POLLING_INTERVAL);
      } catch (error) {
        setIsLoading(false);
        dispatchEvent(
          AgentUIEventTypes.SUBSCRIPTION.PURCHASE_POLLING.COMPLETED,
          {
            msg: 'failure',
            err: error instanceof Error ? error.message : String(error),
          },
        );
        toast.error('Failed to verify subscription status');
      }
    };

    dispatchEvent(AgentUIEventTypes.SUBSCRIPTION.PURCHASE_POLLING.STARTED);
    pollSubscriptionStatus(0);
  };

  const loadingCopyMap = LOADING_COPY_MAP[asyncActionType ?? 'CHECKOUT'];

  const closeModal = () => setIsVisible(false);

  if (isAutofunUser)
    return (
      <AutofunSubscriptionModal
        isOpen={isVisible}
        agentPlan={agentPlan}
        isLoading={isLoading}
        closeModal={closeModal}
        onSubmit={handleOnSubmitClick}
      />
    );

  return (
    <Modal
      isOpen={isVisible}
      closeModal={closeModal}
      modalContainerClassName="p-0 bg-elz-neutral-2 max-w-[384px]"
    >
      <div className="flex flex-col items-center justify-center gap-[8px] border-b-1 border-b-elz-neutral-6 px-[16px] py-[24px]">
        {isLoading ? <LoadingSpinner /> : <Bolt />}

        <p className="text-center text-[18px] font-bold leading-[24px]">
          {isLoading ? loadingCopyMap.title : 'Upgrade your Fleek account'}
        </p>

        <p className="text-center text-[14px] leading-[20px] text-elz-neutral-11 mb-[8px]">
          {isLoading
            ? loadingCopyMap.description
            : 'Add an AI Agent Plan for each agent you deploy'}
        </p>

        {!isLoading && !agentPlan && (
          <Alert variant="danger">
            <p>
              We were unable to load the plan details. Please try again and if
              the issue persists,{' '}
              <Link href="/support" target={Target.Blank} className="underline">
                contact support.
              </Link>
            </p>
          </Alert>
        )}

        {!isLoading && agentPlan && (
          <Box
            className={cn(
              'relative flex w-full flex-col gap-[8px] p-[12px] text-[14px] font-medium leading-[20px] border-1 border-elz-neutral-6 rounded-10 cursor-pointer',
              'border-elz-accent-11 cursor-default',
            )}
          >
            <span>{agentPlan.name}</span>
            <span className="text-elz-neutral-11">{`${formatCurrency(
              agentPlan.price,
            )}/month plus usage costs`}</span>

            {tee && (
              <Link
                href="https://fleek.xyz/guides/understanding-tees-and-sgx-fleek/"
                className="text-elz-accent-11 mt-[8px] flex flex-row gap-[8px] w-fit hover:text-elz-accent-10"
              >
                Learn more{' '}
                <FaArrowRight className="-rotate-45 h-[12px] leading-[12px] relative top-3" />
              </Link>
            )}

            <span className="size-[20px] border-1 border-elz-neutral-6 absolute top-[12px] right-[12px] rounded-[10px]">
              <span
                className={cn(
                  'size-[12px] absolute top-[3px] right-[3px] rounded-[8px]',
                  'bg-elz-accent-11',
                )}
              />
            </span>
          </Box>
        )}

        {!isLoading &&
          !isCreditsPending &&
          !hasAvailableCredits &&
          agentPlan && (
            <Text variant="description" size="sm">
              Want to pay with crypto?{' '}
              <Text
                size="sm"
                className="text-elz-accent-9 cursor-pointer"
                as="span"
                onClick={handleAddCredits}
              >
                Click here
              </Text>{' '}
              to subscribe using your {credits?.formatted} Fleek credit balance.
            </Text>
          )}
      </div>

      <div className="p-[16px]">
        <div className="flex flex-row items-center justify-center gap-[16px] text-[14px] font-medium leading-[20px]">
          {!isLoading && (
            <>
              <button
                type="button"
                className="h-[32px] w-full rounded-[8px] hover:text-elz-white"
                onClick={closeModal}
              >
                No thanks
              </button>
              <Button
                variant="accent"
                className="w-full"
                disabled={isCreatingCheckout || !agentPlan}
                onClick={handleOnSubmitClick}
              >
                Subscribe
              </Button>
            </>
          )}
          {isLoading && (
            <button
              type="button"
              className="h-[32px] w-full rounded-[8px] hover:text-elz-white"
              onClick={closeModal}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

interface SubscriptionModalStore {
  isSubscriptionModalVisible: boolean;
  subscriptionModalCallback?: (value?: boolean) => void;
  tee?: boolean;
  openSubscriptionModal: (args?: { tee?: boolean }) => void;
  setSubscriptionModalCallback: (cb?: (value?: boolean) => void) => void;
  setIsSubscriptionModalVisible: (visible: boolean) => void;
}

export const useSubscriptionModal = create<SubscriptionModalStore>((set) => ({
  isSubscriptionModalVisible: false,
  subscriptionModalCallback: undefined,
  subscriptionAmount: undefined,
  productId: undefined,
  openSubscriptionModal: ({ tee } = {}) =>
    set({
      isSubscriptionModalVisible: true,
      tee,
    }),
  setSubscriptionModalCallback: (cb) =>
    set({
      subscriptionModalCallback: cb,
    }),
  setIsSubscriptionModalVisible: (visible) =>
    set({
      isSubscriptionModalVisible: visible,
    }),
}));
